import React, {Component} from 'react';
import {Link} from "react-router-dom";
import {APIClient} from "../../utils/APIClient";
import {API_URL} from "../../settings/local_settings";
import { withTranslation } from 'react-i18next';
import { withRouterParams } from '../../utils/withRouterParams';
import {AccessService} from "../../utils/AccessService";
import {LocalStorage} from "../../utils/LocalStorage";
import {useFicha21} from "../../context/UserModulesContext";
import {usePendingFactsFor} from "../../context/PendingWorkContext";
import {boosterFlowPath, hasBooster, hasHistorial, isBoosterPath} from "./ia_booster/boosterSteps";
import {quotaFromUserData} from "../../utils/usePortalSubscriptionGating";
import {clientInformesPath} from "../informes/informesPaths";
import {anchorAttr} from "../../guides/anchors";


class ClientTabsClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            appsData: null,
            isSuperAdmin: false,
            hasPortal: null,
        };
    }

    componentDidMount() {
        this.updateAppsTabs();
        this.fetchSuperAdminFlag();
    }

    fetchSuperAdminFlag() {
        new APIClient(API_URL + 'get-logged-user-data/').call('GET').then((data) => {
            if (data && data.status === 'OK') {
                // Persistimos la modalidad en LocalStorage para que ClientPortalTab la lea
                // sin necesidad de un segundo fetch. Default DATA_EXPLORER.
                const modality = data.agency_report_modality || 'DATA_EXPLORER';
                LocalStorage.updateAgencyReportModality(modality, data.agency_id);
                this.setState({
                    isSuperAdmin: data.is_super_admin === true,
                    // «El plan incluye el portal», NO «queda lugar para invitar»: con el cupo lleno
                    // la pestaña desaparecía justo cuando había que mostrar el límite (U5Yuxys2).
                    hasPortal: quotaFromUserData(data)?.hasPortal === true,
                });
            }
        });
    }

    updateAppsTabs() {
        new APIClient(API_URL + 'apps/').call('GET').then((data) => {
            if (data.status !== 'OK') {
                alert(data['error_message']);
            } else {
                this.setState({
                    appsData: data.data
                });
            }
        })
    }

    // `ficha21` (del provider) ya incorpora la preferencia de UI: es false para un usuario que
    // volvió al Dashbo clásico, aunque su agencia sea del tier UX_21. Ese es el contrato del
    // switch — volver al clásico devuelve la ficha vieja ENTERA, no sólo el marco.
    ficha21() {
        return this.props.ficha21 === true;
    }

    hasControlCentral() {
        // El Panel es del rediseño: se va con el switch al clásico. Sin bypass de SuperAdmin — el
        // tier UX_21 ya cubre a las agencias internas, y el bypass rompía la promesa del switch
        // justo para quienes lo usan para QA. El default route sigue siendo Monitoreo (D10).
        return this.ficha21();
    }

    hasModule(moduleName) {
        const modules = this.props.modules;
        if (!modules) return false;
        return modules.includes(moduleName);
    }

    hasActivityLogModule() {
        return this.hasModule(AccessService.MODULE_ACTIVITY_LOG);
    }

    hasActivityLogTab() {
        // Tab standalone legacy (ficha vieja): sigue dogfood SuperAdmin + módulo. Con la ficha 2.1
        // la Bitácora vive DENTRO del tab Memoria (vista 'bitacora') y el gate es SOLO el módulo
        // (D12): el tier ya acota a las agencias del piloto, y el punto es que el equipo de Bakián
        // (no-SuperAdmin) la use y dé feedback.
        return !this.ficha21() && this.state.isSuperAdmin && this.hasActivityLogModule();
    }

    hasHistorialTab() {
        // "Historial" de la 2.1 (card 2BZI0Us8): la Bitácora a pantalla completa, en
        // `/client/:id/activity-log`. Hasta el 24/09 sólo vivía como bandeja del Recorrido y desde
        // el Panel no se encontraba. Gate: `hasHistorial` (boosterSteps.js), el mismo que la ruta.
        // No confundir con `hasStandaloneHistoryTab`: ése es el historial de PRESUPUESTOS.
        return this.ficha21() && hasHistorial({
            clientMemoryLevel: this.props.clientMemoryLevel,
            modules: this.props.modules,
        });
    }

    hasAgentModule() {
        return this.hasModule(AccessService.MODULE_AGENT);
    }

    hasMcpModule() {
        return this.hasModule(AccessService.MODULE_DASHBO_MCP);
    }

    hasIATab() {
        // Tab "IA" unificado (ficha 2.1, D7): Dashbito + Conectar tu IA + Prompts. Con el IA
        // Booster en la barra lateral (paso 6 de gtAL5axI), su puerta es «Usa tu IA» del
        // Recorrido — el tab sale del tab bar. La pantalla y sus rutas quedan intactas; revert =
        // restaurar `this.ficha21() && (this.hasAgentModule() || this.hasMcpModule())`.
        return false;
    }

    hasAgentTab() {
        return !this.ficha21() && this.hasAgentModule();
    }

    hasIAAssistant() {
        // "Conectar con tu IA" legacy (ficha vieja): módulo MCP + SuperAdmin.
        return !this.ficha21() && this.state.isSuperAdmin && this.hasMcpModule();
    }

    hasMemoryLevel() {
        const level = this.props.clientMemoryLevel;
        return level === AccessService.MEMORY_LEVEL_MEDIO || level === AccessService.MEMORY_LEVEL_TOTAL;
    }

    hasMemoryTab() {
        // "Memoria del cliente": visible si la agencia tiene la Memoria habilitada (nivel MEDIO o
        // TOTAL), que se contrata por suscripción — la CAPABILITY no se va con el switch al
        // clásico. También aparece si hay Bitácora aunque la Memoria esté OFF, porque la Bitácora
        // vive ADENTRO de esta pantalla.
        //
        // En la 2.1 la PESTAÑA sale del tab bar (paso 7 de gtAL5axI): su puerta pasa a ser la
        // sección AI Booster de la barra lateral, que replica ESTOS MISMOS gates ítem por ítem
        // (`SidebarV2`). En el clásico —donde no hay barra lateral 2.1— el tab sigue siendo la
        // única puerta y queda como estaba. El enforcement real vive en los endpoints.
        if (this.ficha21()) return false;
        if (this.hasMemoryLevel()) return true;
        return this.hasActivityLogModule();
    }

    hasMediaPlanTab() {
        // "Media plan" (Media Plan Sync): capability del tier IA, NO del rediseño — una agencia
        // sin ficha 2.1 igual lo tiene, y sobrevive al switch al clásico. Hasta la unificación del
        // tier esto se gateaba con `ficha_v2`, que es el rediseño: el gate quedaba un tier más
        // arriba de lo que corresponde. Siempre con módulo MCP; el backend gatea además por
        // MEDIA_PLAN_CONNECTOR_ENABLED.
        //
        // En la 2.1 la PESTAÑA se oculta (card gtAL5axI, paso 7): el media plan pasa a ser una
        // tarea a la que se entra desde el Panel (su card y "Importar media plan"), con la vuelta
        // en su encabezado. La CAPABILITY no se toca — es lo que mantiene el tab vivo en el
        // clásico, donde el Panel no existe. La pantalla y su ruta quedan intactas.
        return this.hasMcpModule() && this.props.mediaPlanEnabled === true && !this.ficha21();
    }

    hasReportsModule() {
        return this.hasModule(AccessService.MODULE_REPORTS);
    }

    hasReportsTab() {
        // Con la navegación 2.1 (D15) Reportes sale del tab bar: Dashbito/reportes quedan
        // fuera del alcance 2.1. La ruta /client/:id/reports sigue viva (deep links).
        return !this.ficha21() && this.hasReportsModule();
    }

    // Navegación 2.1 (D15): el tab bar baja a 7 tabs. Historial se fusiona dentro de Monitoreo y
    // Cuentas + Usuarios dentro de Configuración, como sub-vistas (ver renderSubNav). Ninguna ruta
    // se borra — los deep links y las pantallas quedan intactos. La tab IA se mantiene hasta la
    // Tanda B (decisión fede 30-jul): sin ella, la biblioteca de prompts por cliente y el bloque de
    // Project quedarían sin puerta de entrada.
    hasStandaloneHistoryTab() {
        // En 2.1 el Historial sale del tab bar (card HrTKFvPI): al historial se entra desde CADA
        // línea de control, que ya tiene su acceso propio — una pestaña a nivel cliente para algo
        // que se mira por línea es una parada de más. La ruta `/client/:id/history` queda viva (hay
        // deep links en producción) y marca activo al Panel. En el clásico el tab sigue igual.
        //
        // Fue pestaña propia entre el 26/08 (paso 6 de gtAL5axI) y hoy: revert = `return true`.
        return !this.ficha21();
    }

    hasBoosterTab() {
        // "AI Booster": entrada rápida al Recorrido desde el tab bar (card HrTKFvPI). Convive con
        // el grupo de la barra lateral, que es el recorrido paso a paso — son dos velocidades de
        // la misma pantalla, y sin la pestaña `/client/:id/booster` es la única ruta del cliente
        // donde el tab bar no marca nada (la página monta `ClientTabs` igual).
        //
        // Los gates NO se escriben acá: `hasBooster` es el mismo SSOT que usa `SidebarV2`
        // (`boosterSteps.js`). Un switch propio se desincroniza el día que cambie un nivel.
        return this.ficha21() && hasBooster({
            clientMemoryLevel: this.props.clientMemoryLevel,
            modules: this.props.modules,
        });
    }

    boosterTabClass() {
        // El activo sale del MISMO SSOT que el sidebar, no de `tabClass`: `isPathActive` matchea por
        // substring y esta pregunta ya tiene dueño. Además de la one-page, prende en las pantallas
        // del Recorrido que no migraron (`/memory/setup`, `/ia` con Dashbito) y en el instante
        // previo al redirect de las rutas viejas de Memoria.
        const path = this.props.location?.pathname || '';
        return 'tab-button' + (isBoosterPath(path) ? ' -active' : '');
    }

    // Coexistencia por agencia (frente Informes): la pestaña es Informes cuando la agencia está en
    // esa modalidad. `fetchSuperAdminFlag` ya persistió la modalidad en LocalStorage.
    isInformesModality() {
        return LocalStorage.fetchAgencyReportModality() === 'REPORT_WITH_TEMPLATES';
    }

    hasDataExplorerTab() {
        // Con la modalidad Informes es una solapa REAL de la ficha (`/client/:id/informes`, card
        // 7yIKxqZj): el editor monta este tab bar y la solapa queda marcada. Con la vieja sigue
        // siendo un LINK al Explorador con el cliente ya seleccionado (card HrTKFvPI), que nunca se
        // marca activo: esa pantalla no vive bajo `/client/:id`.
        //
        // Sin el módulo se OCULTA en vez de mostrarse con candado: en 2.1 el marco es `SidebarV2`,
        // que ya dibuja su entrada bloqueada y siempre a la vista. El lugar del upsell es la
        // navegación global, no el tab bar del cliente.
        return this.ficha21() && this.hasModule(AccessService.MODULE_DATA_EXPLORER);
    }

    hasStandaloneTeamTab() {
        return !this.ficha21();
    }

    hasStandaloneAccountsTab() {
        return !this.ficha21();
    }

    // Tab activo por RUTA (clase `-active` en el propio tab). Reemplaza el sistema posicional
    // (-first…-ninth + contador de posiciones), que obligaba a mantener una aritmética frágil
    // cada vez que un tab condicional aparecía o desaparecía.
    isPathActive(fragment) {
        const path = this.props.location?.pathname || '';
        return path.includes(fragment);
    }

    tabClass(...fragments) {
        const active = fragments.some((fragment) => this.isPathActive(fragment));
        return 'tab-button' + (active ? ' -active' : '');
    }

    isClientRoot() {
        // `/client/:id` (con o sin barra final) es la ruta a la que linkea el nombre del cliente en
        // el listado, y en 2.1 sirve el Panel — pero el tab apunta a `/control-central`, así que la
        // comparación por fragmento no matchea y no quedaba NINGUNA pestaña marcada.
        //
        // Va como predicado propio y no como un fragmento más de `tabClass`: `isPathActive` usa
        // `includes`, y el fragmento que haría falta acá es el string vacío — que matchea CUALQUIER
        // ruta y prendería el Panel en todo el tab bar.
        return /^\/client\/\d+\/?$/.test(this.props.location?.pathname || '');
    }

    panelTabClass() {
        if (this.isClientRoot()) return 'tab-button -active';
        // `/lines` es una VISTA del Panel y el media plan es una tarea a la que se entra desde él
        // (card gtAL5axI). El Historial volvió acá al salir del tab bar (card HrTKFvPI): su ruta
        // sigue viva y sin este fragmento sería otra pantalla sin tab marcado. Los fragments extra
        // no pisan al clásico — este tab sólo existe en 2.1. `/alerts` es la pantalla de Alertas a la
        // que lleva el tile «Alertas vigentes» del Panel (card j61xJLod): otra vista suya.
        return this.tabClass('/control-central', '/lines', '/media-plan', '/history', '/alerts');
    }

    monitoringTabFragments() {
        // El tab Monitoreo ya sólo existe en el clásico (card gtAL5axI, paso 7): en 2.1 esas rutas
        // redirigen a la vista de líneas y el Historial pasó a marcar activo al tab Panel.
        return ['/monitoring', '/business-units'];
    }

    configurationTabFragments() {
        // Con la navegación 2.1, Cuentas y Usuarios son sub-vistas de Configuración.
        const fragments = ['/configuration'];
        if (this.ficha21()) fragments.push('/conections', '/dashbo-users', '/crm-users');
        return fragments;
    }

    memoryTabFragments() {
        // La Bitácora vive dentro de Memoria: si se entra por el deep link standalone
        // (/activity-log), el tab Memoria es el que corresponde marcar activo.
        //
        // Se pregunta por el TAB standalone y no por la generación de UI: donde ese tab existe
        // (clásico + SuperAdmin + módulo) es él quien tiene que quedar activo, o `/activity-log`
        // marcaría los dos a la vez. Donde no existe —2.1, o clásico sin SuperAdmin— la única
        // puerta a la Bitácora es Memoria, y este fragmento es lo que la deja marcada.
        const fragments = ['/memory'];
        if (!this.hasActivityLogTab()) fragments.push('/activity-log');
        return fragments;
    }

    // Sub-navegación de las tabs fusionadas (solo navegación 2.1): una fila debajo del tab
    // bar con el mismo lenguaje visual que la sub-navegación de Memoria (.cmem-pill).
    subNavItems() {
        if (!this.ficha21()) return null;
        const groups = [
            // El grupo Monitoreo/Historial ya no existe: /monitoring y /business-units redirigen a
            // /lines (que trae su propio encabezado) y el Historial volvió como pestaña propia
            // (paso 6 de gtAL5axI) — una subnav debajo de un tab de una sola vista es ruido.
            {
                match: ['/configuration', '/conections', '/dashbo-users', '/crm-users'],
                items: [
                    {path: 'configuration', fragments: ['/configuration'], labelKey: 'client_tabs.subnav_general'},
                    {path: 'conections', fragments: ['/conections'], labelKey: 'client_tabs.accounts'},
                    {path: 'dashbo-users', fragments: ['/dashbo-users', '/crm-users'], labelKey: 'client_tabs.team'},
                ],
            },
        ];
        const group = groups.find((g) => g.match.some((fragment) => this.isPathActive(fragment)));
        return group ? group.items : null;
    }

    renderSubNav() {
        const items = this.subNavItems();
        if (!items) return null;
        return (
            <nav className="client-tabs-subnav">
                {items.map((item) => {
                    const active = item.fragments.some((fragment) => this.isPathActive(fragment));
                    return (
                        <Link
                            key={item.path}
                            className={`client-tabs-subnav-pill${active ? ' -active' : ''}`}
                            to={`/client/${this.props.clientId}/${item.path}`}
                        >
                            {this.props.t(item.labelKey)}
                        </Link>
                    );
                })}
            </nav>
        );
    }

    render() {
        return (
            <>
            <div className="filters">
                <ul className="tabs">
                    {this.hasControlCentral() && (
                        <li key="control-central">
                            <Link className={this.panelTabClass()}
                                  to={`/client/${this.props.clientId}/control-central`}>
                                {this.props.t('client_tabs.control_central')}
                            </Link>
                        </li>
                    )}
                    {this.hasHistorialTab() && (
                        <li key="historial">
                            <Link className={this.tabClass('/activity-log')}
                                  to={`/client/${this.props.clientId}/activity-log`}>
                                {this.props.t('client_tabs.activity_history')}
                            </Link>
                        </li>
                    )}
                    {/* En 2.1 la pestaña Monitoreo se OCULTA, no se borra (card gtAL5axI, paso 7):
                        su reemplazo es la vista de líneas del Panel, y /monitoring redirige ahí.
                        En el clásico Monitoreo ES la pantalla de líneas y queda como siempre. */}
                    {!this.ficha21() && (
                        <li key="budgets">
                            <Link id="link_to_budgets" className={this.tabClass(...this.monitoringTabFragments())}
                                  to={`/client/${this.props.clientId}/monitoring`}>
                                {this.props.t('client_tabs.monitoring')}
                            </Link>
                        </li>
                    )}
                    {this.hasStandaloneHistoryTab() && (
                        <li key="history">
                            <Link className={this.tabClass('/history')}
                                  to={`/client/${this.props.clientId}/history`}>
                                {this.props.t('client_tabs.history')}
                            </Link>
                        </li>
                    )}
                    {this.hasBoosterTab() && (
                        <li key="ia-booster">
                            <Link className={this.boosterTabClass()}
                                  to={boosterFlowPath(this.props.clientId)}>
                                {this.props.t('client_tabs.ia_booster')}
                            </Link>
                        </li>
                    )}
                    {this.hasDataExplorerTab() && (this.isInformesModality() ? (
                        <li key="informes">
                            {/* El ancla es el primer paso de las guías de Informes (Aprende Dashbo):
                                la solapa se dibuja mientras el editor todavía carga. */}
                            <Link className={this.tabClass('/informes')}
                                  to={clientInformesPath(this.props.clientId)}
                                  {...anchorAttr('client_tab_informes')}>
                                {this.props.t('client_tabs.informes')}
                            </Link>
                        </li>
                    ) : (
                        <li key="data-explorer">
                            {/* Clase literal, no `tabClass`: esta pestaña LLEVA a otra pantalla y
                                nunca queda activa. Escribirlo así lo hace explícito y testeable.
                                El `?client_id=` es el mecanismo que ya usan el panel clásico y el
                                portal (`DataExplorerUrlParams.readClientIdFromUrl`). */}
                            <Link className="tab-button"
                                  to={`/data-explorer?client_id=${this.props.clientId}`}>
                                {this.props.t('client_tabs.data_explorer')}
                            </Link>
                        </li>
                    ))}
                    {this.hasStandaloneTeamTab() && (
                        <li key="team">
                            <Link className={this.tabClass('/dashbo-users', '/crm-users')}
                                  to={`/client/${this.props.clientId}/dashbo-users`}>
                                {this.props.t('client_tabs.team')}
                            </Link>
                        </li>
                    )}
                    {this.hasStandaloneAccountsTab() && (
                        <li key="conexiones">
                            <Link className={this.tabClass('/conections')} to={`/client/${this.props.clientId}/conections`}>
                                {this.props.t('client_tabs.accounts')}
                            </Link>
                        </li>
                    )}
                    {this.hasActivityLogTab() && (
                        <li key="activity-log">
                            <Link className={this.tabClass('/activity-log')} to={`/client/${this.props.clientId}/activity-log`}>
                                {this.props.t('client_tabs.activity_log')}
                            </Link>
                        </li>
                    )}
                    {this.hasMemoryTab() && (
                        <li key="memory">
                            <Link className={this.tabClass(...this.memoryTabFragments())} to={`/client/${this.props.clientId}/memory`}>
                                {this.props.t('client_tabs.memory')}
                                {/* La cola de curado tiene que verse desde AFUERA del tab: hasta
                                    ahora el badge vivía dentro de Memoria, o sea que había que
                                    entrar para enterarse de que había algo esperando. */}
                                {this.props.pendingFacts > 0 && (
                                    <span className="cmem-pill-badge">{this.props.pendingFacts}</span>
                                )}
                            </Link>
                        </li>
                    )}
                    {this.hasMediaPlanTab() && (
                        <li key="media-plan">
                            <Link className={this.tabClass('/media-plan')} to={`/client/${this.props.clientId}/media-plan`}>
                                {this.props.t('client_tabs.media_plan')}
                            </Link>
                        </li>
                    )}
                    {this.hasReportsTab() && (
                        <li key="reports">
                            <Link className={this.tabClass('/reports')} to={`/client/${this.props.clientId}/reports`}>
                                {this.props.t('client_tabs.reports')}
                            </Link>
                        </li>
                    )}
                    {this.hasAgentTab() && (
                        <li key="agent">
                            <Link className={this.tabClass('/agent')} to={`/client/${this.props.clientId}/agent`}>
                                {this.props.t('client_tabs.agent')}
                            </Link>
                        </li>
                    )}
                    {this.hasIAAssistant() && (
                        <li key="ia-assistant">
                            <Link className={this.tabClass('/ia-assistant')} to={`/client/${this.props.clientId}/ia-assistant`}>
                                {this.props.t('client_tabs.ia_assistant')}
                            </Link>
                        </li>
                    )}
                    {this.hasIATab() && (
                        <li key="ia">
                            {/* '/ia' matchea también '/ia-assistant' y '/media-plan' no: con el
                                flag ON esos tabs legacy no se renderizan, así que no compite. */}
                            <Link className={this.tabClass('/ia')} to={`/client/${this.props.clientId}/ia`}>
                                {this.props.t('client_tabs.ia')}
                            </Link>
                        </li>
                    )}
                    {/* El tab Perfil se quitó (2026-07-06): Industria/KPI/notas los reemplaza la
                        Memoria; "Tipo de negocio" vive en Configuración. La ruta vieja redirige. */}
                    {this.renderAppsTabs()}
                    {this.state.hasPortal && (
                        <li key="portal">
                            <Link className={this.tabClass('/portal-config')} to={`/client/${this.props.clientId}/portal-config`}>
                                {this.props.t('client_tabs.portal')}
                            </Link>
                        </li>
                    )}
                    <li key="configuration">
                        <Link className={this.tabClass(...this.configurationTabFragments())} to={`/client/${this.props.clientId}/configuration`}>
                            {this.props.t('client_tabs.configuration')}
                        </Link>
                    </li>

                </ul>
            </div>
            {this.renderSubNav()}
            </>
        )
    }

    renderAppsTabs() {
        if (this.state.appsData === null)
            return;

        return (this.state.appsData.filter(appData => appData.client_tab).map(appData => (
            <li key={appData.title}>
                <Link className={this.tabClass(`/app/${appData.app_hash}/`)}
                      to={`/client/${this.props.clientId}/app/${appData.app_hash}/`}>
                    {appData.title}
                </Link>
            </li>
        )));
    }
}

const ClientTabsInner = withRouterParams(withTranslation()(ClientTabsClass));

// Los flags salen del provider (fuente única): `ficha21` ya cruza el tier de la agencia con la
// preferencia de UI del usuario. Wrapper funcional porque la clase no puede usar el hook — antes
// esta pantalla hacía su propio fetch de `access/modules`, que era el fetch duplicado más caro
// (abrir el Panel de un cliente lo pedía dos veces).
export function ClientTabs(props) {
    const {ficha21, mediaPlanEnabled, clientMemoryLevel, modules} = useFicha21();
    // El conteo sale del poller global, NO de un fetch propio: el tab bar se monta en todas las
    // pantallas del cliente y pedir la cola en cada una sería el fetch duplicado más caro de la app.
    // El guard de nivel se repite acá aunque el backend ya sólo emita `memory_facts` en TOTAL: sin
    // él, la única razón por la que el badge no aparece en MEDIO vive en el otro repo.
    const pending = usePendingFactsFor(props.clientId);
    const pendingFacts = clientMemoryLevel === AccessService.MEMORY_LEVEL_TOTAL ? pending : 0;
    return <ClientTabsInner {...props} ficha21={ficha21} mediaPlanEnabled={mediaPlanEnabled}
                            clientMemoryLevel={clientMemoryLevel} modules={modules}
                            pendingFacts={pendingFacts}/>;
}
